/**
 * Key under which the last automatic reload is timestamped, so a deployment that
 * is genuinely broken cannot put the tab in a reload loop.
 */
const LAST_RELOAD_KEY = "staleChunkReloadedAt"

/**
 * How long to wait before allowing another automatic reload, in ms.
 */
const RELOAD_COOLDOWN_MS = 30_000

/**
 * Reloads the page once when a route chunk can no longer be fetched.
 *
 * The app is split into content-hashed chunks, and `index.html` is the only thing
 * that knows their names. A deployment replaces both, so any tab still holding the
 * previous `index.html` asks for chunk names the server no longer has: the next
 * lazy route rejects with "Failed to fetch dynamically imported module" and the app
 * dies on a blank screen until the user thinks to hard-refresh.
 *
 * Reloading fetches the current `index.html`, and with it the names of the chunks
 * that actually exist. The cooldown keeps a chunk that is missing for some other
 * reason from looping the tab.
 *
 * Vite raises `vite:preloadError` for exactly this case; the listener also covers
 * the plain rejection, since only dynamic imports routed through Vite's preload
 * helper emit the event.
 */
export function reloadOnStaleChunk(): void {
    const isStaleChunkError = (reason: unknown): boolean =>
        /Failed to fetch dynamically imported module|Importing a module script failed/i.test(
            String((reason as Error | undefined)?.message ?? reason)
        )

    const reloadOnce = (): void => {
        const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0)

        if (Date.now() - last < RELOAD_COOLDOWN_MS) {
            console.error("[chunk] stale chunk after a reload — the deployment is inconsistent")

            return
        }

        sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()))
        window.location.reload()
    }

    window.addEventListener("vite:preloadError", (event) => {
        event.preventDefault()
        reloadOnce()
    })

    window.addEventListener("unhandledrejection", (event) => {
        if (isStaleChunkError(event.reason)) {
            reloadOnce()
        }
    })
}
